import type {
  AdminUser,
  AdminUserDetail,
  EpicEmployeeOption,
  FetchConnectionStatus,
  OnboardingRole,
  ToggleGrant,
  UserRole,
} from '../types/admin';
import { fetchV2WithAuth } from './apiAuth';
import { isTestModeBypass } from './testMode';

// User administration now targets the FranchiseApi v2 backend (/v2/admin/*) on the same
// base URL + x-api-key as the rest of the v2 client. The caller is identified from the
// verified bearer token (Okta/Microsoft), so the legacy X-User-Email header — and the
// callerEmail parameters that fed it — are gone.
const API_BASE_URL = import.meta.env.VITE_FRANCHISE_API_URL;

function apiErrorMessage(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    const error = (parsed as { error?: unknown } | null)?.error;
    return typeof error === 'string' && error.trim() ? error : null;
  } catch {
    return null;
  }
}

class AdminApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL || '';
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    if (!this.baseUrl) throw new Error('Admin API not configured');

    // Every route on this client is behind requireAuth, and a test-mode session holds no
    // bearer token — so in the E2E build these can only 401. Reject locally instead of
    // putting an unanswerable request on a real backend (WIPE-1190); callers already treat
    // a rejection here as "unknown", which is exactly what the 401 used to mean.
    if (isTestModeBypass()) throw new Error('Admin API unavailable in test mode');

    // fetchV2WithAuth attaches auth + x-api-key and replays once on an expired token.
    const response = await fetchV2WithAuth(`${this.baseUrl}${path}`, options);

    if (!response.ok) {
      const text = await response.text();
      // The admin endpoints answer a refusal with `{ error }` written for the person using the page, so
      // show that rather than the status and raw body. Anything else keeps the raw form for debugging.
      throw new Error(apiErrorMessage(text) ?? `API error: ${response.status}. ${text}`);
    }

    return response.json();
  }

  async recordLogin(email: string, name: string, provider: string | null): Promise<AdminUser | null> {
    const result = await this.request<{ user: AdminUser }>('/v2/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, name, provider }),
    });
    return result.user;
  }

  async getMyProfile(): Promise<AdminUser | null> {
    return await this.request<AdminUser>('/v2/me');
  }

  async getProfileByEmail(email: string): Promise<AdminUser | null> {
    return await this.request<AdminUser>(`/v2/admin/users/${encodeURIComponent(email)}`);
  }

  async getUsers(): Promise<{ users: AdminUser[]; count: number }> {
    return this.request('/v2/admin/users');
  }

  async getDirectory(): Promise<{ users: { email: string; name: string; roles: string[] }[]; count: number }> {
    return this.request('/v2/admin/directory');
  }

  async getUserDetail(email: string): Promise<AdminUserDetail> {
    return this.request(`/v2/admin/users/${encodeURIComponent(email)}`);
  }

  async updateUserRole(email: string, role: UserRole): Promise<void> {
    await this.request(`/v2/admin/users/${encodeURIComponent(email)}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async createUser(data: { email: string; name?: string; role?: string; onboarding_in_process?: boolean; new_grad_program?: boolean }): Promise<{ message: string; email: string; role: string }> {
    return this.request('/v2/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserFlags(email: string, flags: { onboarding_in_process?: boolean; new_grad_program?: boolean; self_service_access?: boolean; epic_employee_code?: string | null }): Promise<void> {
    await this.request(`/v2/admin/users/${encodeURIComponent(email)}/flags`, {
      method: 'PUT',
      body: JSON.stringify(flags),
    });
  }

  /**
   * Search Applied Epic employees, to link one to a user.
   *
   * The escape hatch behind `epic_employee_code`: most addresses match exactly one active employee
   * record and link themselves, and this is how the rest are set. Served from FranchiseApi's mirror of
   * Epic's directory, so it answers a keystroke without touching Applied.
   */
  async searchEpicEmployees(query: string): Promise<EpicEmployeeOption[]> {
    const { employees } = await this.request<{ employees: EpicEmployeeOption[] }>(
      `/v2/admin/epic-employees?q=${encodeURIComponent(query)}`
    );
    return employees;
  }

  async updateUserOnboardingRole(email: string, role: OnboardingRole | null): Promise<void> {
    await this.request(`/v2/admin/users/${encodeURIComponent(email)}/onboarding-role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // A grant with one level, so switched on or off rather than set to a role.
  async updateUserGrant(email: string, grant: ToggleGrant, enabled: boolean): Promise<void> {
    await this.request(`/v2/admin/users/${encodeURIComponent(email)}/grants/${grant}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async deleteUser(email: string): Promise<void> {
    await this.request(`/v2/admin/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  }

  // AskFetch underwriting (WIPE-1602): the one connection every assistant answer reads guidelines
  // through. Not cached, because the page shows a state the admin is about to change.
  async getFetchStatus(): Promise<FetchConnectionStatus> {
    return this.request('/v2/admin/fetch/status');
  }

  // Built by the backend, which registers the client with Fetch and holds the PKCE verifier and state.
  async getFetchAuthorizeUrl(): Promise<string> {
    const res = await this.request<{ url: string }>('/v2/admin/fetch/authorize');
    return res.url;
  }

  // Posted with the admin's own bearer token, which is what ties the connection to the admin who
  // started it: Fetch's redirect carries no identity we could trust.
  async connectFetch(code: string, state: string): Promise<FetchConnectionStatus> {
    return this.request('/v2/admin/fetch/connect', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  async disconnectFetch(): Promise<FetchConnectionStatus> {
    return this.request('/v2/admin/fetch', { method: 'DELETE' });
  }

  isConfigured(): boolean {
    return !!this.baseUrl;
  }
}

export const adminApi = new AdminApiClient();
